/**
 * Registration-gated Add to trip.
 *
 * AUTH SEMANTICS (same tri-state as the RN client / @wg/hooks auth core):
 *   'unauthenticated' → no session at all (transient)
 *   'guest'           → AUTHENTICATED session, anonymous user
 *   'authenticated'   → AUTHENTICATED session, REGISTERED user
 * Guests and registered users are BOTH authed — the gate here is on
 * REGISTRATION, not authentication. Never treat 'guest' as "not signed in".
 *
 * Product rule (2026-07-02): saving to a trip requires a REGISTERED account —
 * guests must convert (existing email+password / Google flow; the register
 * endpoint upgrades the guest in place, so their chat + queued adds carry
 * over). Once registered, adds go straight through.
 *
 * Per-instance toggle: `feature_flags.registration_gate: true` turns the
 * gate ON (explore instances). Default OFF — partner embeds keep plain
 * guest adds and never see the register sheet.
 *
 * Returns addItem-compatible fn: adds immediately when allowed, otherwise
 * queues the add and routes to AuthView (returns null → callers treat as
 * "handled, nothing to render yet").
 */
import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { useInstance } from '../context/InstanceContext';
import {
  useItinerary,
  type ItineraryItemInput,
} from '../context/ItineraryContext';
import { useParentBridge } from '../context/ParentBridgeContext';
import { useView } from '../context/ViewContext';
import logger from '../utils/logger';

export function useGatedAddItem() {
  const { authState } = useAuth();
  const { config } = useInstance();
  const { addItem, queuePendingAdd } = useItinerary();
  const { setView } = useView();
  const { requestOpen } = useParentBridge();

  const gateEnabled = config?.feature_flags?.registration_gate === true;
  // Enforcement is OFF by default (relaxed 2026-07). When the gate is on we
  // still offer a VOLUNTARY sign-up (see Footer), but only FORCE registration
  // when `registration_enforce: true` is explicitly set on the instance.
  const enforce = config?.feature_flags?.registration_enforce === true;
  // 'authenticated' = REGISTERED user (guests are also authed — see above).
  const isRegistered = authState === 'authenticated';

  return useCallback(
    (input: ItineraryItemInput) => {
      if (isRegistered || !gateEnabled || !enforce) {
        return addItem(input);
      }
      logger.info('[gated-add] registration required, queueing', {
        title: input.title,
      });
      queuePendingAdd(input);
      // Host-action adds can arrive with the panel closed (e.g. explore map
      // "Add to trip") — surface the auth sheet.
      requestOpen();
      setView('auth');
      return null;
    },
    [
      isRegistered,
      gateEnabled,
      enforce,
      addItem,
      queuePendingAdd,
      requestOpen,
      setView,
    ],
  );
}
